/**
 * Widget V2 — Coach profile (discovery)
 *
 * Bio + what they coach + where, with a CTA that deep-links into a lessons
 * booking pre-filtered to this coach (`/lessons?trainerId=…`).
 */

import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";

import { useWidget } from "/imports/state/WidgetContext";
import { EmptyState, useImageFallback } from "/imports/ui/components/shared";
import { CoachAvatar, fullName, PageHeading, SECTION_LABEL_SX, DISCOVERY_CARD_SX, CATEGORY_ROUTE, useBackTo } from "/imports/ui/components/discovery";

function trainerTeaches(service, trainerId) {
  if (Array.isArray(service.trainerIds) && service.trainerIds.includes(trainerId)) return true;
  return (service.trainers || []).some((t) => t._id === trainerId);
}

// Deep-link a taught service into its booking view, pre-filtered to both the
// service and this coach.
function lessonHref(slug, service, trainerId) {
  const route = CATEGORY_ROUTE[service.categoryType] || "lessons";
  const params = new URLSearchParams();
  if (service.sportId) params.set("sport", service.sportId);
  // Lessons, courts & classes all filter by serviceId so the booking view opens
  // scoped to this exact service rather than its whole category.
  if (route === "lessons" || route === "courts" || route === "classes") params.set("serviceId", service._id);
  params.set("trainerId", trainerId);
  return `/${slug}/${route}?${params.toString()}`;
}

function CoachProfileView() {
  const navigate = useNavigate();
  const { trainerId } = useParams();
  const { slug, trainers, services, sports, locations } = useWidget();

  const goBack = useBackTo(`/${slug}/coaches`);
  const [photoOpen, setPhotoOpen] = useState(false);

  const coach = useMemo(() => (trainers || []).find((t) => t._id === trainerId), [trainers, trainerId]);
  const { showImage: showPhoto, onImageError: onPhotoError } = useImageFallback(coach?.imgUrl);

  const taughtServices = useMemo(
    () => (services || []).filter((s) => s.categoryType === "category-service-private" && trainerTeaches(s, trainerId)),
    [services, trainerId]
  );

  const coachSports = useMemo(() => {
    const ids = new Set(taughtServices.map((s) => s.sportId).filter(Boolean));
    return (sports || []).filter((s) => ids.has(s._id));
  }, [taughtServices, sports]);

  const coachLocations = useMemo(() => (locations || []).filter((l) => (coach?.locationIds || []).includes(l._id)), [locations, coach]);

  if (!coach) {
    return (
      <Box>
        <PageHeading title="Coach" onBack={goBack} />
        <EmptyState
          title="Coach not found"
          description="This coach is no longer listed."
          actionLabel="Back to coaches"
          onAction={() => navigate(`/${slug}/coaches`)}
        />
      </Box>
    );
  }

  const bookWith = () => navigate(`/${slug}/lessons?trainerId=${coach._id}`);
  const bookLabel = `Book with ${coach.firstName || fullName(coach)}`;
  const photoUrl = showPhoto ? coach.imgUrl : "";

  return (
    <Box>
      {/* The heading stays a generic label — the tinted band below owns the
          coach's name so it isn't printed twice. */}
      <PageHeading title="Coach profile" onBack={goBack} />

      <Box sx={{ ...DISCOVERY_CARD_SX, overflow: "hidden" }}>
        {/* Identity band: a soft brand-tinted header (echoes the home hero)
            with a large photo/avatar — this is what keeps the page from
            reading as a grey text sheet. */}
        <Box
          sx={{
            p: 3,
            display: "flex",
            alignItems: "center",
            gap: 2.5,
            background: (t) => `linear-gradient(120deg, ${alpha(t.palette.primary.main, 0.2)} 0%, ${alpha(t.palette.primary.main, 0.05)} 100%)`
          }}
        >
          {photoUrl ? (
            <Box
              onClick={() => setPhotoOpen(true)}
              role="button"
              aria-label={`View ${fullName(coach)}'s photo`}
              sx={{
                cursor: "pointer",
                borderRadius: "50%",
                lineHeight: 0,
                transition: "transform 0.15s ease",
                "&:hover": { transform: "scale(1.03)" }
              }}
            >
              <CoachAvatar trainer={coach} size={104} />
            </Box>
          ) : (
            <CoachAvatar trainer={coach} size={104} />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: "-0.01em" }}>
              {fullName(coach)}
            </Typography>
            {coach.title && (
              <Typography variant="body2" color="text.secondary">
                {coach.title}
              </Typography>
            )}
            {coachSports.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
                {coachSports.map((s) => (
                  <Chip
                    key={s._id}
                    size="small"
                    label={s.name}
                    sx={{ bgcolor: "background.paper", color: "primary.main", fontWeight: 600, boxShadow: "0 1px 4px rgba(26,26,26,0.08)" }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* When the profile has no notes/locations the box only holds the
            desktop CTA, so hide it entirely on mobile in that case. */}
        <Box sx={{ p: 3, display: coach.notes || coachLocations.length > 0 ? "block" : { xs: "none", md: "block" } }}>
          {coach.notes && (
            <>
              <Typography sx={{ ...SECTION_LABEL_SX, mb: 1 }}>About</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line", lineHeight: 1.7 }}>
                {coach.notes}
              </Typography>
            </>
          )}

          {coachLocations.length > 0 && (
            <Box sx={{ mt: coach.notes ? 3 : 0 }}>
              <Typography sx={{ ...SECTION_LABEL_SX, mb: 1 }}>Coaches at</Typography>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                {coachLocations.map((l) => (
                  <Chip key={l._id} size="small" variant="outlined" label={l.name} onClick={() => navigate(`/${slug}/locations/${l._id}`)} />
                ))}
              </Box>
            </Box>
          )}

          {/* Desktop CTA closes the card at the bottom-left, in the content's
              reading column; mobile keeps the full-width block below. */}
          <Box sx={{ mt: coach.notes || coachLocations.length > 0 ? 3 : 0, display: { xs: "none", md: "flex" } }}>
            <Button variant="contained" size="large" onClick={bookWith} sx={{ color: "common.white" }}>
              {bookLabel}
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 3, display: { md: "none" } }}>
        <Button variant="contained" size="large" fullWidth onClick={bookWith} sx={{ py: 1.25, color: "common.white" }}>
          {bookLabel}
        </Button>
      </Box>

      {taughtServices.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ ...SECTION_LABEL_SX, mb: 1.5 }}>Lessons with {coach.firstName || "this coach"}</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {taughtServices.map((s) => (
              <Box
                key={s._id}
                onClick={() => navigate(lessonHref(slug, s, coach._id))}
                sx={{
                  ...DISCOVERY_CARD_SX,
                  p: 2,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  "&:hover": { borderColor: "primary.main" }
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600}>{s.name}</Typography>
                  {s.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                    >
                      {s.description}
                    </Typography>
                  )}
                </Box>
                <ChevronRightIcon sx={{ color: "text.secondary", flexShrink: 0 }} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Dialog open={photoOpen} onClose={() => setPhotoOpen(false)} maxWidth="md">
        <Box sx={{ position: "relative", lineHeight: 0 }}>
          <IconButton
            onClick={() => setPhotoOpen(false)}
            aria-label="Close"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "common.white",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" }
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={photoUrl}
            alt={fullName(coach)}
            onError={onPhotoError}
            sx={{ display: "block", maxWidth: "100%", maxHeight: "90vh", objectFit: "contain" }}
          />
        </Box>
      </Dialog>
    </Box>
  );
}

export default CoachProfileView;
